import ButtonBox from "component/ButtonBox";
import ButtonIcon from "component/ButtonIcon";
import DialogBox from "component/DialogBox";
import Text from "component/Text";
import React, { useState } from "react";

interface IConfirmDialogProps {
  variantType?: "default" | "secondary";
  headerText?: string;
  content?: string;
  containerClassName?: string;
  primaryButtonClassName?: string;
  primaryButtonText?: string;
  primaryButtonColor?: string;
  secondaryButtonText?: string;
  showDialog: boolean;
  closeDialog?: () => void;
  clickToPasteContent?: () => void;
  clickCancel?: () => void;
  primaryButtonClick?: (e?: any) => any;
  primaryButtonIsAsync?: boolean;
}

const ConfirmDialog: React.FC<IConfirmDialogProps> = (props: IConfirmDialogProps) => {
  const {
    variantType,
    showDialog,
    headerText,
    content,
    containerClassName,
    primaryButtonClassName,
    primaryButtonText,
    secondaryButtonText,
    primaryButtonColor,
    clickCancel,
    primaryButtonClick,
    closeDialog,
    primaryButtonIsAsync,
  } = props;

  const [pastedContent, setPastedContent] = useState("");

  const handlePasteClick = () => {
    navigator.clipboard
      .readText()
      .then((pastedText) => {
        // Do something with the pasted text
        setPastedContent(pastedText);
      })
      .catch((err) => {
        console.error("Failed to read clipboard contents: ", err);
      });
  };

  const dialogContainer = () => {
    return (
      <div
        className={`flex w-76 flex-col justify-center rounded-xl bg-light-grey-50 px-6 py-8.5 dark:bg-dark-neutral-gray-200 ${containerClassName ?? ""}`}
      >
        <Text
          label={headerText}
          className="pb-3 text-center text-lg font-semibold dark:text-dark-text-main"
        />

        <div className="w-full pb-6 text-center text-sm text-light-grey-900 dark:text-dark-neutral-gray-900">
          <Text label={content} />
        </div>

        <div className="flex w-full flex-col items-center gap-2">
          <ButtonBox
            className={` ${primaryButtonClassName ?? ""}`}
            color={primaryButtonColor ? primaryButtonColor : ""}
            label={primaryButtonText}
            {...(primaryButtonIsAsync
              ? { onClickWithLoader: primaryButtonClick }
              : { onClick: primaryButtonClick })}
          />

          {secondaryButtonText && (
            <ButtonBox
              className="max-w-fit px-3"
              variantType="no-border"
              label={secondaryButtonText}
              onClick={closeDialog}
            />
          )}
        </div>
      </div>
    );
  };

  const secondaryDialogContainer = () => {
    return (
      <div className="h-89 w-133 rounded-md">
        <div className="flex w-full justify-between rounded-t-md bg-light-grey-50 px-4 py-4.5 dark:bg-dark-neutral-gray-300">
          <div className="text-sm font-semibold leading-none text-light-grey-1000 dark:text-dark-neutral-gray-1000">
            <Text label={"Connect via YAML"} />
          </div>

          <div className="max-w-fit">
            <ButtonIcon className="" type={"PASTE"} onClick={handlePasteClick} />
          </div>
        </div>

        <div className="flex min-h-61">
          <label htmlFor="textarea" className="hidden">
            ''
          </label>
          <textarea
            className="h-61 w-full flex-1 resize-none overflow-y-auto px-4 py-3.5 font-mono font-normal focus:border-none focus:outline-none dark:border-b-dark-neutral-gray-300 dark:border-t-dark-neutral-gray-300 dark:bg-base-container2"
            placeholder="Type or paste YAML code here."
            onChange={(e: any) => {
              setPastedContent(e.target.value);
            }}
            id="textarea"
            value={pastedContent}
          />
        </div>

        <div className="flex w-full justify-end gap-3 rounded-b-md p-3 dark:bg-dark-neutral-gray-300">
          <div className="max-w-fit px-4">
            <ButtonBox
              label={"component.button.cancel"}
              variantType="no-border"
              onClick={() => {
                clickCancel && clickCancel();
                setPastedContent("");
              }}
            />
          </div>

          <div className="w-26">
            <ButtonBox
              label={"component.button.connect"}
              disabled={pastedContent === ""}
              onClick={(event) => {
                setPastedContent("");
                primaryButtonClick && primaryButtonClick(event);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="group grid">
      <DialogBox
        className="popUp-dialog"
        visible={showDialog}
        closable={false}
        draggable={false}
        onHide={() => {
          closeDialog && closeDialog();
        }}
      >
        {variantType === "secondary" ? secondaryDialogContainer() : dialogContainer()}
      </DialogBox>
    </div>
  );
};

export default ConfirmDialog;
